import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  MenuItem,
  Pagination,
  Fade,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle,
  Cancel,
  Info,
  AssignmentInd,
  MedicalInformation,
  HomeWork,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import receptionService from '../services/receptionService';
import ConsultationModal from './ConsultationModal';
import useAuthStore from '../store/authStore'; // Ajoutez cet import
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 10;
const STATUT_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: true, label: 'Terminé' },
  { value: false, label: 'En cours' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusChip = ({ statut }) => (
  <Chip
    icon={statut ? <CheckCircle /> : <Cancel />}
    label={statut ? 'Terminé' : 'En cours'}
    color={statut ? 'success' : 'warning'}
    variant="outlined"
    sx={{ ml: 1 }}
  />
);

const ModuleContent = ({ content }) => (
  <Grid container spacing={2} sx={{ mt: 1 }}>
    {Object.entries(content).map(([key, value]) => (
      <Grid item xs={12} md={6} key={key}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          {value.items ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {value.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {value.description}
              </Typography>
              <List dense>
                {value.items.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{item.name}</span>
                          {item.taux && <Chip label={item.taux} size="small" color="info" />}
                        </Box>
                      }
                      secondary={item.description}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {value.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {value.description}
              </Typography>
            </>
          )}
        </Paper>
      </Grid>
    ))}
  </Grid>
);

const PoliceAccordion = ({ police }) => (
  <Accordion sx={{ my: 1 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MedicalInformation color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {police.label} - {police.numero}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2" paragraph>
        {police.description}
      </Typography>

      {(police.module_police || []).map((module, index) => (
        <Accordion key={index} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="secondary" />
              <Typography variant="subtitle2">{module.label}</Typography>
              <Chip
                label={module.statut ? 'Actif' : 'Inactif'}
                color={module.statut ? 'success' : 'error'}
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              {module.description}
            </Typography>
            <ModuleContent content={module.content} />
          </AccordionDetails>
        </Accordion>
      ))}
    </AccordionDetails>
  </Accordion>
);

const ReceptionCard = ({ reception, onRefresh }) => {
  const [openConsultationModal, setOpenConsultationModal] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const user = useAuthStore(state => state.user);

  const handleDelete = async () => {
    try {
      const response = await receptionService.deleteReception(reception.id);
      if (response.success) {
        toast.success('Réception supprimée avec succès');
        setDeleteConfirmOpen(false);
        onRefresh();
      } else {
        toast.error(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Une erreur est survenue lors de la suppression');
    }
  };

  // Vérification de sécurité pour les données de l'assuré
  const assureData = reception?.assure || {};
  const polices = assureData?.polices || []; // Changé de Polices à polices

  console.log('Reception data:', reception); // Pour déboguer
  console.log('Polices:', polices); // Pour déboguer

  return (
    <Card sx={{ mb: 3, borderLeft: 4, borderColor: reception.statut ? 'success.main' : 'warning.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentInd color="action" />
            <Typography variant="h6" component="div">
              {assureData.nom || ''} {assureData.prenom || ''}
            </Typography>
            <Chip label={`Matricule: ${assureData.matricule || 'N/A'}`} size="small" />
          </Box>
          <StatusChip statut={reception.statut} />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Entrée:</strong> {reception.date_in ? formatDate(reception.date_in) : 'N/A'}
            </Typography>
            {reception.date_out && (
              <Typography variant="body2" color="text.secondary">
                <strong>Sortie:</strong> {formatDate(reception.date_out)}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2">
              <strong>Agent:</strong> {reception.agent || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Partenaire:</strong> {reception.partenaire_type || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {reception.commentaire && (
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Commentaire :
            </Typography>
            <Typography variant="body2">{reception.commentaire}</Typography>
          </Paper>
        )}

        {polices.length > 0 ? (
          polices.map((police) => (
            <PoliceAccordion 
              key={police.id} 
              police={{
                ...police,
                module_police: police.modules || [] // Ajout de cette ligne pour gérer les modules
              }} 
            />
          ))
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            Aucune police d'assurance disponible
          </Alert>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {user?.role === 'medecin_clinique' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenConsultationModal(true)}
            >
              Consultation
            </Button>
          )}
          {user?.role === 'agent_clinique' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Supprimer
            </Button>
          )}
        </Box>

        <ConsultationModal
          open={openConsultationModal}
          onClose={() => setOpenConsultationModal(false)}
          reception={reception}
          onSuccess={onRefresh}
        />

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <Typography>
              Êtes-vous sûr de vouloir supprimer cette réception ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const ReceptionsHistory = () => {
  const [receptions, setReceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalPages, setTotalPages] = useState(1);

  const fetchReceptions = async (pageNumber, search = '', status = 'all') => {
    setLoading(true);
    try {
      const statusParam = status === 'all' ? undefined : status;

      const response = await receptionService.getAllReceptions({
        page: pageNumber,
        limit: ITEMS_PER_PAGE,
        sort: '-date_in',
        search: search?.trim() || '',
        statut: statusParam // Changed from 'status' to 'statut' to match backend
      });

      if (response?.success) {
        setReceptions(response.data || []);
        const total = response.total || response.data?.length || 0;
        setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
        setError(null);
      } else {
        console.error('API error:', response?.message);
        setError(response?.message || "Une erreur est survenue");
        setReceptions([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("Erreur lors du chargement des réceptions");
      setReceptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Create debounced version of fetch
  const debouncedFetch = useMemo(
    () => debounce(fetchReceptions, 300),
    []
  );

  useEffect(() => {
    debouncedFetch(page, searchTerm, statusFilter);
    
    // Cleanup debounce on component unmount
    return () => debouncedFetch.cancel();
  }, [page, searchTerm, statusFilter, debouncedFetch]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(1);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
        Historique des Réceptions
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher par nom, matricule..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              label="Statut"
            >
              {STATUT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : !Array.isArray(receptions) || receptions.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          Aucune réception trouvée
        </Alert>
      ) : (
        <>
          <Fade in={true}>
            <Box>
              {receptions.map((reception) => (
                <ReceptionCard 
                  key={reception?.id || Math.random()} 
                  reception={reception} 
                  onRefresh={() => fetchReceptions(page, searchTerm, statusFilter)}
                />
              ))}
            </Box>
          </Fade>

          {totalPages > 1 && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ReceptionsHistory;